import {
  GIFT_NEW_STATUS_UPDATE_DELAY_MS,
  GiftStatus,
  GiftTabType,
  GiftType,
  GiftsIconDefaultType,
} from './GiftsStore.constants';
import {GiftForUI, GiftStatic, GiftTicket, GiftUserGift, GiftsState, UserGift, UserTicket} from './GiftsStore.types';
import {DI_TOKENS} from '@/di/di.tokens';

import {injected} from 'brandi';

import {ResourcesAll} from '@/resources';
import {ProjectDate} from '@/services/ProjectDate/ProjectDate';
import {GlobalWindow} from '@/types';

import {Store} from '../Store';

export class GiftsStore extends Store<GiftsState> {
  constructor(
    private readonly globalWindow: GlobalWindow,
    private readonly resources: ResourcesAll,
    private readonly projectDate: ProjectDate,
  ) {
    super({
      activeTab: GiftTabType.Gifts,
      gifts: [],
      tickets: [],
      seenGiftsIds: [],
      defaultGiftIconTypes: [],
      isHasNewGifts: false,
      isShowResultsGiftModal: false,
    });
  }

  public setActiveTabGifts = (): void => {
    this.setValue('activeTab', GiftTabType.Gifts);
  };

  public setActiveTabUserGifts = (): void => {
    this.setValue('activeTab', GiftTabType.UserGifts);
  };

  public setGifts = (gifts: UserGift[]): void => {
    this.setValue('gifts', this.mapGifts(gifts));
  };

  public pushGifts(gifts: UserGift[]): void {
    this.setValue('gifts', [...this.mapGifts(gifts), ...this.state.gifts]);
  }

  private mapGifts(gifts: UserGift[]): GiftUserGift[] {
    return gifts.map<GiftUserGift>((g) => {
      return {
        ...g,
        type: GiftType.UserGift,
        isActive: this.projectDate.isAfterNow(g.activeBefore),
        isPending: g.status === GiftStatus.Pending,
        iconType: g.iconType || this.getDefaultGiftIconType(),
      };
    });
  }

  public setTickets = (tickets: UserTicket[]): void => {
    this.setValue('tickets', tickets);
  };

  public getActiveTab = (): GiftTabType => {
    return this.getState().activeTab;
  };

  public getGifts = (): GiftUserGift[] => {
    const {gifts} = this.getState();

    return this.sortGiftsByActive(gifts);
  };

  public getGiftTickets = (): GiftTicket => {
    const {tickets} = this.getState();

    return {
      type: GiftType.Ticket,
      tickets,
    };
  };

  public getSeenGiftsIds = (): string[] => {
    return this.getState().seenGiftsIds;
  };

  public isActiveTabGifts = (): boolean => {
    return this.getState().activeTab === GiftTabType.Gifts;
  };

  public isActiveTabUserGifts = (): boolean => {
    return this.getState().activeTab === GiftTabType.UserGifts;
  };

  public static isGiftTypeStatic = (gift: GiftForUI): gift is GiftStatic => {
    return gift.type === GiftType.Static;
  };

  public static isGiftTypeUserGift = (gift: GiftForUI): gift is GiftUserGift => {
    return gift.type === GiftType.UserGift;
  };

  public isHaveTickets = (): boolean => {
    return this.getState().tickets.length > 0;
  };

  public setHasNewGifts(value: boolean): void {
    this.setValue('isHasNewGifts', value);
  }

  public getHasNewGifts(): boolean {
    return this.state.isHasNewGifts;
  }

  public setShowResultsGiftModal = (value: boolean): void => {
    this.setValue('isShowResultsGiftModal', value);
  };

  public isShowResultsGiftModal = (): boolean => {
    return this.state.isShowResultsGiftModal;
  };

  public getHasNewGiftsByGiftList = (): boolean => {
    return this.getState().gifts.some((g) => g.isNew);
  };

  public onNewGiftViewIntersection = (giftId: string): void => {
    const {gifts, seenGiftsIds} = this.getState();
    const gift = gifts.find((g) => g.id === giftId);

    if (gift) {
      this.setValue('seenGiftsIds', [...seenGiftsIds, giftId]);
      this.globalWindow.setTimeout(() => {
        this.updateGiftIsNew(giftId);
      }, GIFT_NEW_STATUS_UPDATE_DELAY_MS);
    }
  };

  public clearSeenGiftsIds = (): void => {
    this.setValue('seenGiftsIds', []);
  };

  private updateGiftIsNew = (giftId: string): void => {
    const {gifts} = this.getState();

    if (gifts.find((g) => g.id === giftId)) {
      this.setValue(
        'gifts',
        gifts.map((gift) => {
          if (gift.id === giftId) {
            return {
              ...gift,
              isNew: false,
            };
          }
          return gift;
        }),
      );
    }

    this.setHasNewGifts(this.getHasNewGiftsByGiftList());
  };

  private sortGiftsByActive = (gifts: GiftUserGift[]): GiftUserGift[] => {
    const activeGifts: GiftUserGift[] = [];
    const inactiveGifts: GiftUserGift[] = [];

    gifts.forEach((gift) => {
      if (gift.isActive) {
        activeGifts.push(gift);
      } else {
        inactiveGifts.push(gift);
      }
    });

    return [...activeGifts, ...inactiveGifts];
  };

  private getDefaultGiftIconType = (): GiftsIconDefaultType => {
    if (this.getState().defaultGiftIconTypes.length === 0) {
      this.setValue('defaultGiftIconTypes', [...this.resources.content.GiftIcon.defaultIconTypes]);
    }

    const {defaultGiftIconTypes} = this.getState();

    const lastIndex = defaultGiftIconTypes.length - 1;
    const iconType = defaultGiftIconTypes[lastIndex];
    this.setValue('defaultGiftIconTypes', defaultGiftIconTypes.slice(0, lastIndex));

    return iconType;
  };
}

injected(GiftsStore, DI_TOKENS.globalWindow, DI_TOKENS.resources, DI_TOKENS.projectDate);
